<template>
  <SectionShell id="switch" num="01" kicker="The Switch">
    <template #title>You already know how to<br />draw. You don't yet know<br />how to <span class="hl">look</span>.</template>
    <template #lede>
      <p>
        Ask anyone to draw an eye from memory and you get the same picture: a pointed
        almond, a circle inside it, spokes for lashes. That picture is a <b>symbol</b> —
        a compressed label your brain filed away in childhood. It is fast, it is
        confident, and it is nothing like an eye.
      </p>
    </template>

    <div class="grid">
      <div class="grid__demo" v-reveal>
        <WipeCompare before-label="symbol drawing" after-label="observed drawing">
          <template #before><HeadOutline /></template>
          <template #after><HeadForm :light-x="0.72" :light-y="0.24" /></template>
          <template #caption>
            Drag the handle. Same skull, same features, same proportions — the only
            difference is that the right half was built out of values and the left
            half was built out of lines.
          </template>
        </WipeCompare>
      </div>

      <div class="grid__text">
        <article class="law" v-reveal="80">
          <span class="law__n mono">Law 01</span>
          <h3>Nothing in nature has a line around it.</h3>
          <p>
            An outline is a fence you build because you're not sure where the form ends.
            In reality an edge is just the place where one value stops and another
            starts. Draw the values and the edge appears for free. Draw the fence and
            you get a cartoon, no matter how carefully you shade inside it.
          </p>
        </article>

        <article class="law" v-reveal="160">
          <span class="law__n mono">Law 02</span>
          <h3>You are not drawing a face.</h3>
          <p>
            You are drawing an arrangement of light and dark shapes that happen to add
            up to a face. The moment your brain says <i>"that's a nose"</i>, it stops
            looking and starts reciting. Every technique in the next nine chapters is a
            trick for keeping your brain from naming what your eye is copying.
          </p>
        </article>

        <article class="law" v-reveal="240">
          <span class="law__n mono">Law 03</span>
          <h3>Your eyes must ping-pong.</h3>
          <p>
            Beginners look at the reference, then turn to the paper and draw from the
            three seconds of memory they just made. Look, mark, look, mark — the glance
            back should be almost annoyingly frequent. Accuracy is a function of how
            often you check, not how steady your hand is.
          </p>
        </article>
      </div>
    </div>

    <aside class="callout" v-reveal>
      <div class="callout__k mono">The one-sentence version</div>
      <p class="callout__t">
        Realism is not a talent you're born with. It's the habit of copying
        <span class="sig">shapes</span> and <span class="hl">values</span> instead of
        copying <span class="dim">names</span>.
      </p>
    </aside>
  </SectionShell>
</template>

<script setup>
import SectionShell from '../ui/SectionShell.vue'
import WipeCompare from '../ui/WipeCompare.vue'
import HeadOutline from '../svg/HeadOutline.vue'
import HeadForm from '../svg/HeadForm.vue'
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  gap: clamp(28px, 4vw, 64px);
  align-items: start;
}
.grid__demo { position: sticky; top: 96px; }

.law { padding: 26px 0; border-top: 1px solid var(--edge-soft); }
.law:first-child { border-top: 0; padding-top: 0; }
.law__n { color: var(--signal); display: block; margin-bottom: 12px; font-size: 10px; }
.law h3 {
  font-size: clamp(20px, 1.5vw + 14px, 29px);
  margin-bottom: 14px;
  font-weight: 400;
  letter-spacing: -0.02em;
}
.law p { color: var(--text-dim); }
.law i { color: var(--v4); font-style: italic; }

.callout {
  margin-top: clamp(44px, 6vh, 80px);
  border-left: 1px solid var(--light);
  padding: 26px 0 26px 30px;
  background: linear-gradient(90deg, var(--light-dim), transparent 60%);
}
.callout__k { color: var(--light); margin-bottom: 14px; font-size: 10px; }
.callout__t {
  font-size: clamp(20px, 2.1vw + 12px, 38px);
  line-height: 1.25;
  letter-spacing: -0.025em;
  color: var(--v5);
  max-width: 26ch;
  font-weight: 300;
}
.callout__t .sig { color: var(--signal); }
.callout__t .hl { color: var(--light); }
.callout__t .dim { color: var(--v2); text-decoration: line-through; }

@media (max-width: 980px) {
  .grid { grid-template-columns: 1fr; }
  .grid__demo { position: static; }
}
</style>
